import React, { useCallback, useEffect, useState } from 'react';
import { Dispatch } from 'src/components-bl/types';
import { CustomInspirationsGalleryLayoutType } from 'src/services';
import { CustomInspirationGalleryDraft } from '../../../../types';
import { GalleryGridContainerStyled, ImageGridStyled } from './GalleryGrid.styles';
import { GalleryGridItem } from './components';
import { useFetchProductsInBulk } from 'src/components-bl/Galleries/CustomInspirations/hooks/useFetchProductsInBulk';
import { GalleryMapper } from '../../../../mapper';

interface GalleryGridProps {
  gallery: CustomInspirationGalleryDraft<CustomInspirationsGalleryLayoutType.Grid>;
  onImageClick: (imageId: string) => void;
  onAddTags: (imageId: string) => void;
  onRemoveImage: (imageId: string) => void;
  onChange: (partialDraft: Partial<CustomInspirationGalleryDraft>) => void;
  dispatch: Dispatch;
  shopId: number;
  onImageLoaded: () => void;
  hide: boolean;
}

export function GalleryGrid({
  gallery,
  onAddTags,
  onImageClick,
  onRemoveImage,
  onChange,
  dispatch,
  shopId,
  onImageLoaded,
  hide,
}: GalleryGridProps): JSX.Element {
  const [draggedAndDroppedImages, setDraggedAndDroppedImages] = useState([...gallery.images]);

  useEffect(() => {
    setDraggedAndDroppedImages(gallery.images);
  }, [gallery.images]);

  const onImageDrag = useCallback(
    (sourceIndex: number, targetIndex: number) => {
      setDraggedAndDroppedImages(previousImages => {
        const rearrangedImages = [...previousImages];
        const elementToMove = rearrangedImages[sourceIndex];

        rearrangedImages.splice(sourceIndex, 1);
        rearrangedImages.splice(targetIndex, 0, elementToMove);

        return rearrangedImages;
      });
    },
    [setDraggedAndDroppedImages]
  );

  const onImageDrop = useCallback(() => {
    onChange({ images: draggedAndDroppedImages });
  }, [draggedAndDroppedImages, onChange]);

  const { productInformationLookup, fetchProductsInBulk, resetOptions, loading } =
    useFetchProductsInBulk({
      dispatch,
      shopId,
      initialLoading: true,
    });

  const onRemoveTagFromImage = useCallback(
    ({ imageId, tagId }: { imageId: string; tagId: string }) => {
      const updatedImages = draggedAndDroppedImages.map(image => {
        if (image.id === imageId) {
          return {
            ...image,
            tags: image.tags.filter(tag => tag.id !== tagId),
            aiSuggestedTags: image.aiSuggestedTags.filter(tag => tag.id !== tagId),
          };
        }

        return image;
      });

      onChange({ images: updatedImages });
    },
    [onChange, draggedAndDroppedImages]
  );

  useEffect(() => {
    resetOptions();

    const skus = GalleryMapper.getUniqueSkusFromImages(gallery.images);

    fetchProductsInBulk(skus);
  }, [gallery.images]);

  const numberOfGridColumns =
    gallery.layout.type === CustomInspirationsGalleryLayoutType.Grid
      ? gallery.layout.gridImagesPerRow
      : gallery.layout.carouselNumberOfImages;

  return (
    <GalleryGridContainerStyled hide={hide}>
      <ImageGridStyled variant='standard' columns={numberOfGridColumns} gap={16}>
        {draggedAndDroppedImages.map((image, index) => (
          <GalleryGridItem
            key={image.id}
            image={image}
            index={index}
            onImageClick={onImageClick}
            onAddTags={onAddTags}
            onRemoveImage={onRemoveImage}
            onImageDrag={onImageDrag}
            onImageDrop={onImageDrop}
            shopId={shopId}
            dispatch={dispatch}
            onImageLoaded={onImageLoaded}
            loading={loading}
            productInformationLookup={productInformationLookup}
            onRemoveTagFromImage={onRemoveTagFromImage}
          />
        ))}
      </ImageGridStyled>
    </GalleryGridContainerStyled>
  );
}
